<script lang="ts">
	import {
		getDeviceSettings,
		getDeviceStatus,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	// Formatters
	import { format } from 'svelte-i18n';
	// Svelte functions
	import { onDestroy, onMount } from 'svelte';
	import { get } from 'svelte/store';
	// Stores
	import { app, os } from 'src/stores/session/native-bridge';
	import { brpcResponse } from 'src/stores/features/ble/brpc';
	import {
		controlBleDevice,
		suppressBleControlSetupModal,
		suppressBleSessionTeardownAlerts,
	} from 'src/stores/features/ble/control-device';
	// Services
	import {
		control_bluetooth_device_ios,
		send_brpc_ios,
	} from 'src/services/native_container_requests_ios';
	import {
		control_bluetooth_device_android,
		send_brpc_android,
	} from 'src/services/native_container_requests_android';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	// Helpers
	import { capFirstChar } from 'src/helpers/utilities';
	import { copy } from 'src/helpers/copy_text';
	import { real_id } from 'src/helpers/interface_helpers';
	import { isFwAfter200, resolveBridgeBleMac } from 'src/helpers/ble_devices_helpers';
	// Types
	import type { Device } from 'src/types/Device';
	// Constants
	import { BLE_PAIRING_TIMEOUT_SEC } from 'src/constants';
	// Design system components
	import {
		Alert,
		AlertV2,
		Button,
		ButtonV2,
		Collapse,
		IconButton,
		Input,
		InputV2,
		Loader,
		Select,
		SelectV2,
		Text,
		toast,
	} from '@Shelly/shelly-design-system';
	import type { SelectOptionPropsType } from '@Shelly/shelly-design-system';
	import SettingGuide from '../../SettingGuide.svelte';

	$: settingsStore = getDeviceSettings(device.id);
	$: statusStore = getDeviceStatus(device.id);
	export let device: Device;
	export let isOffline = false;
	/**
	 * Forwarded by `DeviceSettingsItem` / `deviceSettingFormatter`. We use
	 * `useNewLayout` to swap V1 design-system primitives (device drawer) for
	 * their V2 counterparts (side panel). `test_setting` is consumed by the
	 * parent `DeviceSettingsItem`; we declare it to avoid Svelte's
	 * "unknown prop" dev warning.
	 */
	export let useNewLayout = false;
	// svelte-ignore unused-export-let
	export let test_setting = false;

	/**
	 * Button & Input props line up between V1 and V2, so we can safely render
	 * them through `<svelte:component>`. Alert and Select have meaningfully
	 * different APIs (no `type` on AlertV2, different `change` payload on
	 * SelectV2), so they're branched explicitly in the markup below.
	 */
	const ButtonC = useNewLayout ? ButtonV2 : Button;
	const InputC = useNewLayout ? InputV2 : Input;

	interface PredefinedCommand {
		label: string;
		method: string;
		params: Record<string, unknown>;
	}

	interface HistoryEntry {
		id: number;
		method: string;
		frame: string;
		response: string | null;
		error: string | null;
		startedAt: number;
		durationMs: number | null;
	}

	const PREDEFINED_COMMANDS: PredefinedCommand[] = [
		{ label: 'Shelly.GetDeviceInfo', method: 'Shelly.GetDeviceInfo', params: {} },
		{ label: 'Shelly.GetConfig', method: 'Shelly.GetConfig', params: {} },
		{ label: 'Shelly.GetStatus', method: 'Shelly.GetStatus', params: {} },
		{ label: 'WiFi.GetStatus', method: 'WiFi.GetStatus', params: {} },
		{
			label: 'Cloud.SetConfig — enable cloud',
			method: 'Cloud.SetConfig',
			params: { config: { enable: true } },
		},
		{
			label: 'WiFi.SetConfig — connect to Wi-Fi',
			method: 'WiFi.SetConfig',
			params: { config: { sta: { enable: true, ssid: '', pass: '', roam_interval: 900 } } },
		},
	];

	const isNativeApp = $app;
	const isIOS = get(os) === 'IOS';
	/**
	 * Devices on FW ≥2.0.0 support `Ble.StartPairing`/`Ble.StopPairing`, so
	 * when the native bridge reports `pairingModeRequired` we can drive
	 * the firmware-level pairing ourselves and re-attempt the connect.
	 * Older firmware doesn't expose those RPCs, so for those devices we
	 * just keep the plain connect and surface the section as-is — RPC
	 * over BLE simply won't be available without a successful bond.
	 */
	const supportsPairingRpc = isFwAfter200(readDeviceSettings(device.id));

	/** Ble.GetStatus `mac`/`addr` when present; Shelly.GetStatus `ble` is preferred via store. */
	let bleMacFromRpc = '';
	let bleAddrFromRpc = '';

	const deviceRealId = real_id(device.id);

	$: bleDeviceMac = resolveBridgeBleMac(device, {
		statusMac: $statusStore?.ble?.mac,
		statusAddr: $statusStore?.ble?.addr,
		rpcMac: bleMacFromRpc,
		rpcAddr: bleAddrFromRpc,
	});

	const fetchStatusBleMac = async () => {
		if (isOffline) return;
		try {
			const res = await gen2Request(device, 'Ble.GetStatus', {});
			if (res?.isok) {
				const data = (res as { data?: { mac?: string; addr?: string } })?.data ?? res;
				bleMacFromRpc = (data as { mac?: string })?.mac ?? '';
				bleAddrFromRpc = (data as { addr?: string })?.addr ?? '';
			}
		} catch (error) {
			console.error('Failed to fetch BLE MAC for RPC tester', error);
		}
	};

	let selectedIndex = 0;
	let customMethod = '';
	let customParamsInput = '{}';
	let loading = false;
	let pendingId: number | null = null;
	/**
	 * Last frame we sent — kept around so the auto-pair flow can resend it
	 * transparently once bonding completes. Cleared on success or hard
	 * failure so we never loop indefinitely.
	 */
	let pendingFrame: { id: number; method: string; frame: string } | null = null;
	let history: HistoryEntry[] = [];
	let customParamsError = '';
	let openHistoryId: number | null = null;
	let connectionError = '';
	let connectionErrorKind: '' | 'connect' = '';
	/**
	 * True while we're driving the Gen2 firmware-level pairing flow
	 * (`Ble.StartPairing` → reconnect → `Ble.StopPairing`). Drives both
	 * the "Pairing & retrying…" UI label and the one-shot guard that
	 * prevents looping when pairing still doesn't take.
	 */
	let pairingInProgress = false;
	/**
	 * Set to `true` once we've consumed a `pairingModeRequired` status for
	 * the current session. Reset each time we successfully connect or the
	 * user closes/reopens the section, so the next first-time pairing
	 * request triggers the auto-pair flow once.
	 */
	let pairingAttempted = false;
	/**
	 * Watchdog flag: flipped on by the on-mount/retry timer when the
	 * native bridge sits at `searchingShellyDevice` (or earlier) past
	 * `CONNECT_STUCK_TIMEOUT_MS` without progressing to connected. This
	 * usually means iOS is holding stale bonding credentials for this
	 * device, so we surface a manual Retry CTA plus an instruction to
	 * forget the device from system Bluetooth settings.
	 */
	let isStuck = false;
	let stuckTimer: ReturnType<typeof setTimeout> | null = null;
	const CONNECT_STUCK_TIMEOUT_MS = 12000;

	/**
	 * Timestamp of the last `brpcResponse` frame we consumed. Used to
	 * gate the response reactive block so it only fires on truly new
	 * frames — without this, the second `sendFrame()` triggers the
	 * reactive immediately (because `pendingId` just changed) and
	 * applies the *previous* response to the new history entry.
	 */
	let lastHandledBrpcTs = 0;
	/**
	 * `true` once the native bridge has reached `connectedToDevice` at
	 * least once during this mount. Used to gate the auto-reconnect
	 * subscription so it only fires for sessions that previously
	 * succeeded — we don't want to spam re-connects before the first
	 * connect even finishes.
	 */
	let hasEverConnected = false;
	/**
	 * `true` once at least one BRPC frame has produced a successful
	 * response during this mount. Used to gate the auto-pair recovery
	 * flow: before the first response we still want auto-pair to run
	 * on `pairingModeRequired` (it's the genuine "you need to bond"
	 * signal); after the first response, the same status is just part
	 * of the post-teardown cascade the native bridge fires on every
	 * frame, so triggering `runAutoPairAndRetry` would needlessly
	 * replace the form with the "Pairing…" banner on each send.
	 */
	let hasReceivedBrpcResponse = false;
	/** Wall-clock timer for the auto-reconnect-after-disconnect flow. */
	let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
	/**
	 * Hard ceiling on how long a single `sendFrame()` is allowed to keep
	 * `loading === true` without a matching `brpcResponse`. Prevents the
	 * UI from getting stuck on "Waiting for response…" if the native
	 * bridge silently drops a frame — we mark the history entry with an
	 * inline timeout error and clear `pendingId` ourselves rather than
	 * letting a noise error tear it down.
	 */
	const SEND_TIMEOUT_MS = 30000;
	let sendTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
	/**
	 * `true` once the native bridge has reported `deviceDisconnected` for a
	 * FW ≥2.0.0 device — e.g. the user cancelled the iOS pair prompt, or
	 * the link dropped post-frame. We can only run BRPC against a *paired*
	 * device, so until the next successful `connectedToDevice` we disable
	 * every control and close any open pairing window on the device.
	 */
	let deviceDisconnected = false;

	$: selectedCommand = PREDEFINED_COMMANDS[selectedIndex];
	$: predefinedOptions = PREDEFINED_COMMANDS.map<SelectOptionPropsType>((cmd, i) => ({
		value: String(i),
		title: cmd.label,
	}));
	$: selectedOption = predefinedOptions[selectedIndex];

	/**
	 * `controlBleDevice.connectionState`: 0 = disconnected, 1 = connecting, 2 = connected.
	 * `currentErrorIndex >= 0` means the native bridge reported a control error.
	 */
	$: isConnected = $controlBleDevice.connectionState === 2;
	$: isConnecting = $controlBleDevice.connectionState === 1;
	/**
	 * The native bridge tears down the BLE link after every BRPC frame, so
	 * a strict `isConnected` gate would disable the form between sends
	 * while the auto-reconnect timer ramps the session back up. Once we've
	 * had at least one successful connect (`hasEverConnected`) we trust
	 * the session is recoverable and keep controls enabled — the
	 * pre-send reconnect/native bridge handles any transient gap.
	 *
	 * The `!loading` gate is FW ≥2.0.0-only. On those devices the native
	 * bridge reliably emits a matching `brpcResponse` for every frame, so
	 * blocking a second click while one is in flight is safe and prevents
	 * accidental double-sends. Older firmware doesn't always emit that
	 * event for every frame, so we leave the controls enabled mid-send
	 * to avoid stranding the user behind a stuck "Sending…" state — the
	 * send-watchdog (`armSendTimeout`) still surfaces an inline timeout
	 * error on the history entry if a response truly never arrives.
	 */
	$: canSend =
		isNativeApp &&
		!isOffline &&
		(isConnected || hasEverConnected) &&
		(!supportsPairingRpc || !loading) &&
		!pairingInProgress &&
		!deviceDisconnected;

	/**
	 * Identifiers we'll accept on the response `src` field. The native
	 * bridge fires the same `brpcResponse` store for every BLE-connected
	 * device, so we have to filter on something — and the response's
	 * `src` (shape: `${model}-${mac}`) is the only field that reliably
	 * identifies the responder. We can't lean on the request `id` echo
	 * because some firmwares (observed on FW <2.0.0 Gen2) ignore the
	 * client id and stamp their own Unix-seconds timestamp instead.
	 */
	$: deviceSrcMatchers = [device.id, (device as { mac?: string }).mac, bleDeviceMac].filter(
		(v): v is string => typeof v === 'string' && v.length > 0,
	);

	/**
	 * Listen for BLE RPC frames coming back from the native bridge.
	 *
	 * We gate on three things to keep stale or unrelated frames out of
	 * the current history entry:
	 *  1. `timestamp` — only consume *new* frames, so a second
	 *     `sendFrame()` doesn't immediately re-enter this block while
	 *     `$brpcResponse` still holds the previous response.
	 *  2. `pendingId` — there must be an in-flight request to apply to.
	 *  3. Frame `src` (when present) — must reference this device. This
	 *     blocks the cross-mount/cross-device leak where the store
	 *     might still carry another device's frame; same-mount stale
	 *     frames are already prevented by the `brpcResponse.set(null)`
	 *     reset in `onMount` / `onDestroy`.
	 */
	$: if (
		$brpcResponse &&
		$brpcResponse.timestamp !== lastHandledBrpcTs &&
		pendingId !== null &&
		frameMatchesDevice($brpcResponse.frame, deviceSrcMatchers)
	) {
		lastHandledBrpcTs = $brpcResponse.timestamp;
		applyResponse($brpcResponse.frame);
	}

	/**
	 * Status / error indices from `status_keys` / `error_keys` in
	 * `src/helpers/ble_control_helpers.ts`. The store records translation
	 * keys (e.g. `pairing_mode_required`) rather than raw native names,
	 * so we match by index — same pattern the global handler uses.
	 */
	const STATUS_PAIRING_MODE_REQUIRED = 4;
	const STATUS_CONNECTED_TO_DEVICE = 20;
	const ERR_CANT_CONNECT_BLUETOOTH = 9;
	const ERR_UNPAIR_DEVICE_REQUIRED = 10;
	const ERR_CONNECTION_TIMEOUT = 11;
	const ERR_BONDING_FAILED = 24;

	/**
	 * Trigger the firmware-level pairing flow when the native bridge
	 * reports `pairingModeRequired` (status index 4) — that's the device
	 * telling us "you need to bond before I'll accept RPC". The GBLE
	 * "press button" modal is suppressed for this view, so we drive the
	 * recovery ourselves via cloud RPC (`Ble.StartPairing`) and let iOS
	 * surface its own system pairing prompt during the reconnect.
	 *
	 * Only available on FW ≥2.0.0 (`Ble.StartPairing` was introduced
	 * there). Older firmware just stays connected without pairing — RPC
	 * over BLE simply won't work there, and that's expected.
	 */
	$: if (
		$controlBleDevice.currentStatusIndex === STATUS_PAIRING_MODE_REQUIRED &&
		supportsPairingRpc &&
		!pairingInProgress &&
		!pairingAttempted &&
		!hasReceivedBrpcResponse
	) {
		pairingAttempted = true;
		void runAutoPairAndRetry();
	}

	/**
	 * Same auto-pair recovery, but triggered by an error path instead of
	 * a status: on first connection to a Gen2 device that's never been
	 * bonded with this phone, the native bridge often answers a Send with
	 * `unpairDeviceRequired` (index 10) followed by `bondingFailed`
	 * (index 24) rather than the cleaner `pairingModeRequired` status
	 * above. Either error tells us the same thing — the firmware needs a
	 * bond before it will accept the BRPC — so we drive the same
	 * `Ble.StartPairing` → reconnect → `Ble.StopPairing` flow, which
	 * surfaces the system pair prompt and resends the in-flight frame
	 * once the bond completes.
	 *
	 * Gated on `loading` so we only react to errors that arrive *for an
	 * active send* (otherwise the same errors fired as routine
	 * background bookkeeping between sessions would needlessly kick off
	 * the pair flow), and on `!hasReceivedBrpcResponse` so after BRPC is
	 * proven working the post-teardown cascade — which raises these same
	 * errors after every frame on FW ≥2.0.0 — doesn't re-trigger.
	 */
	$: if (
		($controlBleDevice.currentErrorIndex === ERR_UNPAIR_DEVICE_REQUIRED ||
			$controlBleDevice.currentErrorIndex === ERR_BONDING_FAILED) &&
		supportsPairingRpc &&
		loading &&
		!pairingInProgress &&
		!pairingAttempted &&
		!hasReceivedBrpcResponse
	) {
		pairingAttempted = true;
		void runAutoPairAndRetry();
	}

	/**
	 * Once we actually connect, the recovery loop is done — clear the
	 * one-shot guard so a future re-mount or session disruption can run
	 * the pairing flow again if the firmware asks for it. Also raise
	 * `hasEverConnected` so the auto-reconnect subscription knows it's
	 * safe to re-open the session if the bridge drops it later, and
	 * clear `deviceDisconnected` so the form re-enables after a
	 * successful pair on the next attempt.
	 */
	$: if ($controlBleDevice.currentStatusIndex === STATUS_CONNECTED_TO_DEVICE) {
		pairingAttempted = false;
		pairingInProgress = false;
		hasEverConnected = true;
		deviceDisconnected = false;
	}

	/**
	 * FW ≥2.0.0 only: react to the `deviceDisconnected` native status by
	 * disabling every select/button until we're back on a live session.
	 * The flag clears on the next `connectedToDevice` (reactive above),
	 * so the post-BRPC re-handshake driven from `applyResponse` re-enables
	 * the form once the link is back up; if the disconnect was a genuine
	 * dead-end (e.g. the user dismissed the OS pair prompt), the controls
	 * stay disabled until the user re-opens the section or hits Retry.
	 *
	 * The wind-down of in-flight UI state (`pairingInProgress`, `loading`,
	 * `pendingId`/`pendingFrame`) plus the best-effort `Ble.StopPairing`
	 * only run when we're actually inside the auto-pair flow — outside
	 * of pairing we don't want to drop a still-pending response or stop
	 * a pairing window someone else is using.
	 *
	 * `currentStatus` is matched as a raw string because
	 * `deviceDisconnected` is not present in `status_keys`, so it maps to
	 * `currentStatusIndex === -1` and we can't index-match here.
	 */
	$: if (
		supportsPairingRpc &&
		$controlBleDevice.currentStatus === 'deviceDisconnected' &&
		!deviceDisconnected
	) {
		deviceDisconnected = true;
		if (pairingInProgress) {
			pairingInProgress = false;
			pairingAttempted = false;
			loading = false;
			pendingId = null;
			pendingFrame = null;
			gen2Request(device, 'Ble.StopPairing', {}).catch(() => {
				/* best-effort — the device may already be unreachable */
			});
		}
	}

	/**
	 * React to control errors reported via the global `bleControlHandler`.
	 *
	 * Destructive paths (clearing `pendingId`/`pendingFrame`, surfacing
	 * the inline "Bluetooth session unavailable" banner) only run when
	 * we're NOT inside the post-BRPC noise window:
	 *  - `loading === true` means a frame is in flight; errors here are
	 *    routinely raced ahead of the actual `brpcResponse` by the native
	 *    bridge's teardown cascade, so unwinding now would drop the
	 *    response (the `brpcResponse` reactive gates on `pendingId`).
	 *  - `hasReceivedBrpcResponse === true` means BRPC is proven working
	 *    for this mount; every further `bondingFailed` /
	 *    `cantConnectToBluetooth` / `connectionTimeout` is just the
	 *    one-shot teardown the bridge fires on each frame, and replacing
	 *    the form with the error banner would block back-to-back sends.
	 *
	 * The send-watchdog (`armSendTimeout`) is the safety net that frees
	 * the UI if a frame truly is lost. Genuine *first-time* connection
	 * failures still surface, since both gates are false on the first
	 * mount before any Send.
	 */
	$: {
		const errIdx = $controlBleDevice.currentErrorIndex;
		const inPostBrpcWindow = loading || hasReceivedBrpcResponse;
		if (errIdx === ERR_BONDING_FAILED && !inPostBrpcWindow) {
			loading = false;
			pendingId = null;
			pendingFrame = null;
			pairingInProgress = false;
			pairingAttempted = false;
		} else if (
			(errIdx === ERR_CANT_CONNECT_BLUETOOTH || errIdx === ERR_CONNECTION_TIMEOUT) &&
			!inPostBrpcWindow
		) {
			connectionErrorKind = 'connect';
			connectionError = $format('brpc_error_connect_failed', {
				default: 'Could not establish a Bluetooth session with the device.',
			});
			loading = false;
			pendingId = null;
			pendingFrame = null;
			pairingInProgress = false;
			pairingAttempted = false;
		} else if (errIdx === null || errIdx === -1) {
			connectionError = '';
			connectionErrorKind = '';
		}
	}

	function isValidJson(value: string): boolean {
		try {
			JSON.parse(value);
			return true;
		} catch {
			return false;
		}
	}

	function buildFrame(method: string, params: Record<string, unknown>): { id: number; frame: string } {
		const id = Date.now();
		const frame = JSON.stringify({ id, src: 'shelly-app', method, params });
		return { id, frame };
	}

	function formatResponseText(raw: unknown): string {
		if (typeof raw === 'string') {
			try {
				return JSON.stringify(JSON.parse(raw), null, 2);
			} catch {
				return raw;
			}
		}
		return JSON.stringify(raw, null, 2);
	}

	/**
	 * Best-effort device check before we accept a `brpcResponse` frame
	 * as the answer to the current in-flight request. The response
	 * `src` (shape: `${model}-${mac}`) is the only field that reliably
	 * identifies the responder across firmware versions — request id
	 * echo is not honoured by every firmware (FW <2.0.0 Gen2 stamps
	 * its own Unix-seconds id on the response), so we don't gate on
	 * id at all and instead require `src` to contain one of the
	 * device's known identifiers (id / mac / BLE mac). Frames without
	 * a parseable `src` fall through to the timestamp + `pendingId`
	 * gating in the reactive so we don't drop legitimate responses
	 * from bridges that surface a different envelope shape.
	 */
	function frameMatchesDevice(rawFrame: unknown, matchers: string[]): boolean {
		if (matchers.length === 0) return true;
		let frame: unknown = rawFrame;
		if (typeof frame === 'string') {
			try {
				frame = JSON.parse(frame);
			} catch {
				return true;
			}
		}
		if (!frame || typeof frame !== 'object') return true;
		const src = (frame as { src?: unknown }).src;
		if (typeof src !== 'string') return true;
		const lowerSrc = src.toLowerCase();
		return matchers.some((m) => lowerSrc.includes(m.toLowerCase()));
	}

	function applyResponse(rawFrame: unknown): void {
		clearSendTimeout();
		const responseText = formatResponseText(rawFrame);
		history = history.map((entry) =>
			entry.id === pendingId
				? {
						...entry,
						response: responseText,
						durationMs: Date.now() - entry.startedAt,
					}
				: entry,
		);
		pendingId = null;
		pendingFrame = null;
		pairingAttempted = false;
		loading = false;
		/**
		 * Mark that BRPC is proven working for this mount so the auto-pair
		 * reactive ignores the `pairingModeRequired` status that the post-
		 * teardown cascade fires on every subsequent reconnect — otherwise
		 * each send would briefly replace the form with the pairing banner.
		 */
		hasReceivedBrpcResponse = true;
		/**
		 * FW ≥2.0.0 only: the native bridge always tears down the BLE link
		 * after a successful frame, so eagerly re-open it now — by the
		 * time the user clicks Send again the link is already coming back
		 * up, and we don't need to lean on the 500ms
		 * `scheduleAutoReconnect` window or risk the controls flashing
		 * disabled on the next click. Older firmware doesn't need this
		 * (the link generally stays alive between frames there).
		 */
		if (supportsPairingRpc && isNativeApp) {
			startConnect();
		}
	}

	/**
	 * Sends a frame over the native BLE bridge. The frame is also cached
	 * in `pendingFrame` so the auto-pair flow can resend it transparently
	 * after bonding succeeds.
	 */
	function dispatchFrameToNative(frame: string): void {
		if (isIOS) {
			send_brpc_ios(device.id, frame);
		} else {
			send_brpc_android(bleDeviceMac, frame);
		}
	}

	function sendFrame(method: string, params: Record<string, unknown>): void {
		const { id, frame } = buildFrame(method, params);
		const entry: HistoryEntry = {
			id,
			method,
			frame,
			response: null,
			error: null,
			startedAt: Date.now(),
			durationMs: null,
		};
		history = [entry, ...history].slice(0, 10);
		pendingId = id;
		pendingFrame = { id, method, frame };
		pairingAttempted = false;
		loading = true;
		/** Auto-expand the new entry so the response is visible without a click. */
		openHistoryId = id;
		armSendTimeout();
		dispatchFrameToNative(frame);
	}

	function sendPredefined(): void {
		sendFrame(selectedCommand.method, selectedCommand.params);
	}

	function sendCustom(): void {
		const trimmed = customMethod.trim();
		if (!trimmed) {
			customParamsError = $format('brpc_error_method_required', {
				default: 'Method name is required.',
			});
			return;
		}
		if (!isValidJson(customParamsInput)) {
			customParamsError = $format('brpc_error_params_invalid', {
				default: 'Params must be valid JSON.',
			});
			return;
		}
		customParamsError = '';
		sendFrame(trimmed, JSON.parse(customParamsInput));
	}

	function copyEntry(text: string): void {
		copy(text, $format('copied_successfully', { default: 'Copied successfully!' }));
	}

	function clearHistory(): void {
		history = [];
		pendingId = null;
		openHistoryId = null;
		loading = false;
	}

	/**
	 * Handle the preset Select change event. V1 dispatches
	 * `{ value: string }`, V2 dispatches the option itself (string or
	 * `{ value }` object), so we normalise both shapes here.
	 */
	function handlePresetChange(event: CustomEvent<unknown>): void {
		const detail = event.detail as string | { value?: unknown } | null;
		const raw = detail && typeof detail === 'object' && 'value' in detail ? detail.value : detail;
		const next = Number(raw);
		if (!Number.isNaN(next)) selectedIndex = next;
	}

	function toggleHistory(id: number): void {
		openHistoryId = openHistoryId === id ? null : id;
	}

	function historySubtitle(entry: HistoryEntry): string {
		if (entry.response === null && entry.error === null) {
			return $format('brpc_history_waiting', { default: 'Waiting for response…' });
		}
		if (entry.durationMs !== null) {
			return $format('brpc_history_completed', {
				default: 'Completed in {ms} ms',
				values: { ms: entry.durationMs },
			});
		}
		return $format('brpc_history_completed_no_time', { default: 'Completed' });
	}

	/**
	 * Triggers a plain connect against the native bridge. We never
	 * pass `bonding_required=true` from this view: for FW ≥2.0.0 the
	 * native bridge fires `pairingModeRequired` on its own when bonding
	 * is needed, and we recover by driving `Ble.StartPairing` over
	 * cloud RPC before re-attempting the connect (see
	 * `runAutoPairAndRetry`). For older firmware the connect is all
	 * we can do — RPC over BLE simply won't work without bonding, but
	 * the section is still usable for testing the connection itself.
	 */
	function startConnect(): void {
		if (isIOS) {
			control_bluetooth_device_ios(device.id, 'connect');
		} else {
			control_bluetooth_device_android(bleDeviceMac, 'connect');
		}
	}

	function disconnectNative(): void {
		if (!isNativeApp) return;
		isIOS
			? control_bluetooth_device_ios(device.id, 'disconnect')
			: control_bluetooth_device_android(bleDeviceMac, 'disconnect');
	}

	function clearSendTimeout(): void {
		if (sendTimeoutTimer) {
			clearTimeout(sendTimeoutTimer);
			sendTimeoutTimer = null;
		}
	}

	/**
	 * Arms the send-watchdog so a frame that never produces a
	 * `brpcResponse` (silent native drop, etc.) doesn't strand the form
	 * on "Waiting for response…". On expiry we attach an inline timeout
	 * error to the still-pending history entry and clear `pendingId`
	 * ourselves — by this point any post-teardown error from the native
	 * bridge has long since been ignored by the gated error reactive,
	 * so this is the path that ultimately frees up the form.
	 */
	function armSendTimeout(): void {
		clearSendTimeout();
		sendTimeoutTimer = setTimeout(() => {
			sendTimeoutTimer = null;
			if (!loading || pendingId === null) return;
			const stuckId = pendingId;
			history = history.map((entry) =>
				entry.id === stuckId
					? {
							...entry,
							error: $format('brpc_error_no_response', {
								default: 'No response from device.',
							}),
							durationMs: Date.now() - entry.startedAt,
						}
					: entry,
			);
			loading = false;
			pendingId = null;
			pendingFrame = null;
		}, SEND_TIMEOUT_MS);
	}

	function clearStuckTimer(): void {
		if (stuckTimer) {
			clearTimeout(stuckTimer);
			stuckTimer = null;
		}
	}

	function clearReconnectTimer(): void {
		if (reconnectTimer) {
			clearTimeout(reconnectTimer);
			reconnectTimer = null;
		}
	}

	/**
	 * When the native bridge tears down the BLE session after a frame
	 * (iOS in particular tends to drop the link once the RPC has been
	 * delivered), schedule a transparent reconnect so the user can
	 * keep sending commands without manual intervention. Bails out if
	 * a pairing/error/stuck flow is already managing the connection.
	 */
	function scheduleAutoReconnect(): void {
		if (!isNativeApp) return;
		if (pairingInProgress || connectionError || isStuck) return;
		clearReconnectTimer();
		reconnectTimer = setTimeout(() => {
			reconnectTimer = null;
			const s = get(controlBleDevice);
			if (s.connectionState !== 0 || pairingInProgress || connectionError || isStuck) {
				return;
			}
			startConnect();
			armStuckTimer();
		}, 500);
	}

	/**
	 * Arms the watchdog that surfaces a "stuck" UI if the native bridge
	 * doesn't reach `connectionState === 2` within `CONNECT_STUCK_TIMEOUT_MS`.
	 * We only flip `isStuck` when we're *still* not connected and no
	 * inline error was raised — otherwise the existing error path owns the
	 * UX and the watchdog should bow out.
	 */
	function armStuckTimer(): void {
		clearStuckTimer();
		stuckTimer = setTimeout(() => {
			stuckTimer = null;
			if (get(controlBleDevice).connectionState !== 2 && !connectionError) {
				isStuck = true;
			}
		}, CONNECT_STUCK_TIMEOUT_MS);
	}

	/**
	 * Whenever the connection succeeds or surfaces an inline error, the
	 * watchdog has nothing left to do — clear the flag and the timer so
	 * the next mount / retry starts fresh.
	 */
	$: if (isConnected || connectionError) {
		clearStuckTimer();
		if (isConnected) isStuck = false;
	}

	/**
	 * Manual recovery for stuck sessions. Issues an
	 * `abortConnecting`/`disconnect` to coax the native bridge out of its
	 * stale state, resets our store snapshot, then re-arms the connect
	 * and watchdog as if we'd just mounted.
	 */
	function retryConnect(): void {
		if (!isNativeApp) return;
		/**
		 * Clear flags & timers that would otherwise either bail out the
		 * reconnect (`connectionError`/`isStuck`) or race against it
		 * (`reconnectTimer`).
		 */
		isStuck = false;
		connectionError = '';
		connectionErrorKind = '';
		clearStuckTimer();
		clearReconnectTimer();
		const state = get(controlBleDevice);
		if (state.connectionState === 1) {
			isIOS
				? control_bluetooth_device_ios(device.id, 'abortConnecting')
				: control_bluetooth_device_android(bleDeviceMac, 'disconnect');
		} else {
			disconnectNative();
		}
		/**
		 * Give the native bridge a beat to settle before re-opening the
		 * session — connecting too quickly after disconnect can leave
		 * iOS in a state where it ignores the new request.
		 */
		setTimeout(() => {
			controlBleDevice.reset();
			controlBleDevice.updateDeviceId(device.id);
			startConnect();
			armStuckTimer();
		}, 600);
	}

	/**
	 * Resolves when the native bridge reports `connectionState === 2`
	 * (`connectedToDevice`), or rejects after `timeoutMs` if the
	 * connection never completes. Rejects early on a fresh control error
	 * so the caller can short-circuit pairing flows.
	 */
	function waitForConnected(timeoutMs = 15000): Promise<void> {
		return new Promise((resolve, reject) => {
			let settled = false;
			const baselineErrorTs = get(controlBleDevice).lastTimestamp;
			const unsubscribe = controlBleDevice.subscribe((state) => {
				if (settled) return;
				if (state.connectionState === 2) {
					settled = true;
					unsubscribe();
					clearTimeout(timer);
					resolve();
				} else if (state.currentError && state.lastTimestamp > baselineErrorTs) {
					settled = true;
					unsubscribe();
					clearTimeout(timer);
					reject(new Error(state.currentError));
				}
			});
			const timer = setTimeout(() => {
				if (settled) return;
				settled = true;
				unsubscribe();
				reject(new Error('timeout'));
			}, timeoutMs);
		});
	}

	function notifyPairingFailed(title: string, text: string): void {
		toast.open({ type: 'danger', title, text });
		loading = false;
		pendingId = null;
		pendingFrame = null;
		pairingInProgress = false;
		pairingAttempted = false;
	}

	/**
	 * Recovery path for `pairingModeRequired` (status index 4) on
	 * FW ≥2.0.0. Puts the Gen2 firmware into pairing mode via cloud RPC
	 * (`Ble.StartPairing`), tears down the unbonded native session, then
	 * reconnects so iOS/Android can surface their own system pairing
	 * prompt and complete the bond. Once we reach `connectedToDevice`
	 * we close pairing mode and resend any in-flight frame.
	 *
	 * The GBLE-style "press device button for 10 seconds" modal is
	 * suppressed for the duration of this view via
	 * `suppressBleControlSetupModal` — mains-powered Gen2 devices enter
	 * pairing mode via RPC, not a physical button press.
	 */
	async function runAutoPairAndRetry(): Promise<void> {
		if (pairingInProgress) return;
		pairingInProgress = true;
		/**
		 * If a frame is mid-flight we keep its "Waiting for response…"
		 * UI visible while we pair; otherwise the spinner stays off.
		 */
		if (pendingFrame) loading = true;

		let pairingStarted = false;
		try {
			/** 1. Put the device firmware into pairing mode. */
			const startRes = await gen2Request(device, 'Ble.StartPairing', {
				timeout: BLE_PAIRING_TIMEOUT_SEC,
			});
			if (!startRes?.isok) {
				throw new Error('start_pairing_failed');
			}
			pairingStarted = true;

			/**
			 * 2. Tear down the unbonded native session so the reconnect
			 *    triggers a fresh OS-level handshake.
			 */
			disconnectNative();
			await new Promise<void>((r) => setTimeout(r, 500));
			controlBleDevice.updateConnectionState(0);
			controlBleDevice.updateDeviceId(device.id);

			/**
			 * 3. Reconnect. With the firmware in pairing mode the OS
			 *    will surface its own system pairing prompt (not the
			 *    GBLE in-app modal — that one is suppressed here).
			 */
			startConnect();
			await waitForConnected(20000);

			/** 4. End pairing mode now that we're bonded. */
			await gen2Request(device, 'Ble.StopPairing', {});
			pairingStarted = false;

			pairingInProgress = false;

			/**
			 * 5. If the user already had a frame in flight when we
			 *    started, resend it transparently. Reset the entry's
			 *    timer so `durationMs` reflects the retry, not the
			 *    abandoned first attempt.
			 */
			if (pendingFrame) {
				const retryFrame = pendingFrame.frame;
				const retryId = pendingFrame.id;
				history = history.map((entry) =>
					entry.id === retryId ? { ...entry, startedAt: Date.now() } : entry,
				);
				dispatchFrameToNative(retryFrame);
			} else {
				loading = false;
			}
		} catch (err) {
			console.error('Bluetooth RPC auto-pair failed', err);
			/**
			 * Best-effort cleanup: always try to close pairing mode if we
			 * opened it, so the device doesn't sit advertising for 2min.
			 */
			if (pairingStarted) {
				try {
					await gen2Request(device, 'Ble.StopPairing', {});
				} catch {
					/* ignore — device may already be unreachable */
				}
			}
			notifyPairingFailed(
				$format('brpc_toast_pair_failed_title', {
					default: 'Bluetooth pairing failed',
				}),
				$format('brpc_toast_pair_failed_text', {
					default:
						'The device still reports as not paired after a pairing attempt. Please remove it from your phone Bluetooth settings and try again.',
				}),
			);
		}
	}

	/**
	 * Auto-reconnect subscription: watches the native connectionState
	 * and re-opens the BLE session whenever it falls back to 0 after
	 * we've previously been fully connected (i.e. the bridge dropped
	 * the link on us, typically after delivering a BRPC frame). This
	 * keeps the form's controls enabled for back-to-back commands
	 * without the user having to retry manually.
	 */
	let prevConnectionState = 0;
	let unsubAutoReconnect: (() => void) | null = null;

	onMount(() => {
		fetchStatusBleMac();
		if (!isNativeApp) return;
		/**
		 * Reset any store residue from a previous mount/abort so the
		 * reactive blocks don't act on stale `connectionState`/error.
		 *
		 * The `brpcResponse` store is module-scoped and survives between
		 * mounts. If we don't clear it here, the first `sendFrame()` on
		 * the new mount finds `$brpcResponse.timestamp !== 0` and runs
		 * `applyResponse` against the previous device's frame, so the
		 * first answer rendered for the new device is the cached one
		 * from the previous session.
		 */
		controlBleDevice.reset();
		brpcResponse.set(null);
		lastHandledBrpcTs = 0;
		controlBleDevice.updateDeviceId(device.id);
		/**
		 * FW ≥2.0.0 devices drive their own pairing via `Ble.StartPairing`,
		 * so the GBLE-style "press device button for 10 seconds" modal
		 * would be misleading — suppress it for the duration of this view.
		 * Older firmware never reaches the auto-pair flow, so leaving the
		 * global modal active there is fine.
		 */
		if (supportsPairingRpc) {
			suppressBleControlSetupModal.set(true);
		}
		/**
		 * Opt out of the global handler's reaction to the post-BRPC
		 * teardown cascade (`unpairDeviceRequired` → `bondingFailed` on
		 * FW ≥2.0.0). The native bridge surfaces both as routine
		 * background bookkeeping after every successful frame, and the
		 * default reaction (force-disconnect + danger toast) would block
		 * back-to-back sends with alerts the user can't act on inline.
		 */
		suppressBleSessionTeardownAlerts.set(true);
		/**
		 * Watch for connected → disconnected transitions and schedule a
		 * transparent reconnect. Subscribing in `onMount` (rather than
		 * using a `$:` block) avoids re-firing on every reactive
		 * invalidation and keeps the prev-state bookkeeping local.
		 */
		unsubAutoReconnect = controlBleDevice.subscribe((state) => {
			const prev = prevConnectionState;
			prevConnectionState = state.connectionState;
			if (prev === 2 && state.connectionState === 0 && hasEverConnected) {
				scheduleAutoReconnect();
			}
		});
		/**
		 * Defensive: prior sessions can leave iOS holding an in-flight
		 * Bluetooth handle that silently swallows the next connect
		 * (we observe the bridge sitting at `searchingShellyDevice`
		 * forever). Fire-and-forget an abort first, then connect once
		 * the native bridge has had a beat to settle.
		 */
		isIOS
			? control_bluetooth_device_ios(device.id, 'abortConnecting')
			: control_bluetooth_device_android(bleDeviceMac, 'disconnect');
		setTimeout(() => {
			startConnect();
			armStuckTimer();
		}, 400);
	});

	onDestroy(() => {
		/**
		 * Reset any in-flight UI state so leftover history/pending entries
		 * don't bleed into the next mount of this section.
		 */
		loading = false;
		pendingId = null;
		pendingFrame = null;
		pairingInProgress = false;
		pairingAttempted = false;
		isStuck = false;
		hasEverConnected = false;
		hasReceivedBrpcResponse = false;
		lastHandledBrpcTs = 0;
		deviceDisconnected = false;
		/**
		 * Clear the cross-mount store too so a subsequent mount on a
		 * different device doesn't pick up this session's last frame
		 * as its first "response".
		 */
		brpcResponse.set(null);
		clearStuckTimer();
		clearReconnectTimer();
		clearSendTimeout();
		if (unsubAutoReconnect) {
			unsubAutoReconnect();
			unsubAutoReconnect = null;
		}
		/** Re-enable the global pair modal & teardown alerts for other surfaces. */
		suppressBleControlSetupModal.set(false);
		suppressBleSessionTeardownAlerts.set(false);
		if (!isNativeApp) {
			controlBleDevice.reset();
			return;
		}
		const state = get(controlBleDevice);
		if (state.connectionState === 2) {
			isIOS
				? control_bluetooth_device_ios(device.id, 'disconnect')
				: control_bluetooth_device_android(bleDeviceMac, 'disconnect');
		} else if (state.connectionState === 1) {
			/**
			 * Mirror PairAndTroubleShoot.svelte: iOS supports a dedicated
			 * abort, Android handles it via plain `disconnect`.
			 */
			isIOS
				? control_bluetooth_device_ios(device.id, 'abortConnecting')
				: control_bluetooth_device_android(bleDeviceMac, 'disconnect');
		}
		controlBleDevice.reset();
	});
</script>

<SettingGuide
	text={$format('settings_guide_bluetooth_rpc_tester', {
		default:
			'Send test commands to this device over Bluetooth to check that RPC over BLE is working.',
	})}
/>

<!-- Connection / availability banner -->
{#if !isNativeApp}
	{#if useNewLayout}
		<AlertV2
			class="mb-m"
			customIcon="fas fa-exclamation-triangle"
			heading={$format('brpc_native_only_heading', { default: 'Native app required' })}
			message={$format('brpc_native_only_message', {
				default:
					'This tester routes RPC calls through the native Bluetooth bridge. Open this device in the Shelly Smart Control app on iOS or Android to send real commands.',
			})}
			isHeadingBold
		/>
	{:else}
		<Alert
			class="mb-m"
			type="warning"
			heading={$format('brpc_native_only_heading', { default: 'Native app required' })}
			message={$format('brpc_native_only_message', {
				default:
					'This tester routes RPC calls through the native Bluetooth bridge. Open this device in the Shelly Smart Control app on iOS or Android to send real commands.',
			})}
			isHeadingBold
		/>
	{/if}
{:else if connectionError}
	{#if useNewLayout}
		<AlertV2
			class="mb-s"
			customIcon="fas fa-exclamation-triangle"
			heading={$format('brpc_connection_error_heading', {
				default: 'Bluetooth session unavailable',
			})}
			message={connectionError}
			isHeadingBold
		/>
	{:else}
		<Alert
			class="mb-s"
			type="danger"
			heading={$format('brpc_connection_error_heading', {
				default: 'Bluetooth session unavailable',
			})}
			message={connectionError}
			isHeadingBold
		/>
	{/if}
	<div class="d-flex justify-content-end mb-m">
		<svelte:component this={ButtonC} variant="primary" on:click={retryConnect}>
			{$format('brpc_button_retry', { default: 'Retry' })}
		</svelte:component>
	</div>
{:else if isStuck}
	{#if useNewLayout}
		<AlertV2
			class="mb-s"
			customIcon="fas fa-exclamation-triangle"
			heading={$format('brpc_stuck_heading', { default: 'Bluetooth is not responding' })}
			message={$format('brpc_stuck_message', {
				default:
					"We couldn't open a Bluetooth session with the device. If you've previously paired it with this phone, open the system Bluetooth settings, forget the device, then tap Retry.",
			})}
			isHeadingBold
		/>
	{:else}
		<Alert
			class="mb-s"
			type="warning"
			heading={$format('brpc_stuck_heading', { default: 'Bluetooth is not responding' })}
			message={$format('brpc_stuck_message', {
				default:
					"We couldn't open a Bluetooth session with the device. If you've previously paired it with this phone, open the system Bluetooth settings, forget the device, then tap Retry.",
			})}
			isHeadingBold
		/>
	{/if}
	<div class="d-flex justify-content-end mb-m">
		<svelte:component this={ButtonC} variant="primary" on:click={retryConnect}>
			{$format('brpc_button_retry', { default: 'Retry' })}
		</svelte:component>
	</div>
{:else if pairingInProgress}
	{#if useNewLayout}
		<AlertV2
			class="mb-m"
			heading={$format('brpc_pairing_heading', { default: 'Pairing with device…' })}
			message={$format('brpc_pairing_message', {
				default:
					'Putting the device into pairing mode and bonding over Bluetooth. The original command will be re-sent automatically.',
			})}
		/>
	{:else}
		<Alert
			class="mb-m"
			type="info"
			heading={$format('brpc_pairing_heading', { default: 'Pairing with device…' })}
			message={$format('brpc_pairing_message', {
				default:
					'Putting the device into pairing mode and bonding over Bluetooth. The original command will be re-sent automatically.',
			})}
		/>
	{/if}
{:else if isConnecting}
	{#if useNewLayout}
		<AlertV2
			class="mb-m"
			heading={$format('brpc_connecting_heading', { default: 'Connecting over Bluetooth…' })}
			message={$format('brpc_connecting_message', {
				default: 'Opening a BLE session with the device. This usually takes a few seconds.',
			})}
		/>
	{:else}
		<Alert
			class="mb-m"
			type="info"
			heading={$format('brpc_connecting_heading', { default: 'Connecting over Bluetooth…' })}
			message={$format('brpc_connecting_message', {
				default: 'Opening a BLE session with the device. This usually takes a few seconds.',
			})}
		/>
	{/if}
{:else if isConnected}
	{#if useNewLayout}
		<AlertV2
			class="mb-m"
			heading={$format('brpc_active_heading', { default: 'Connected over Bluetooth' })}
			message={$format('brpc_active_message', {
				default:
					'A BLE session to this device is active for the duration of this view. RPC calls below are sent via the native bridge.',
			})}
		/>
	{:else}
		<Alert
			class="mb-m"
			type="info"
			heading={$format('brpc_active_heading', { default: 'Connected over Bluetooth' })}
			message={$format('brpc_active_message', {
				default:
					'A BLE session to this device is active for the duration of this view. RPC calls below are sent via the native bridge.',
			})}
		/>
	{/if}
{:else if useNewLayout}
	<AlertV2
		class="mb-m"
		heading={$format('brpc_idle_heading', { default: 'Preparing Bluetooth session' })}
		message={$format('brpc_idle_message', {
			default: 'Hang tight while we get the device ready for RPC over BLE.',
		})}
	/>
{:else}
	<Alert
		class="mb-m"
		type="info"
		heading={$format('brpc_idle_heading', { default: 'Preparing Bluetooth session' })}
		message={$format('brpc_idle_message', {
			default: 'Hang tight while we get the device ready for RPC over BLE.',
		})}
	/>
{/if}

<!-- Predefined command section -->
<Text weight="bold">
	{$format('brpc_predefined_title', { default: 'Predefined RPC' })}
</Text>
<Text styleType="body-small" color="secondary" class="mt-xs mb-s">
	{$format('brpc_predefined_subtitle', { default: 'Quickly send a common Shelly RPC method.' })}
</Text>

<div class="brpc-stack mb-m">
	{#if useNewLayout}
		<SelectV2
			id="brpc-preset"
			icon={null}
			aria-label={$format('brpc_preset_aria', { default: 'Predefined command' })}
			label={$format('brpc_preset_label', { default: 'Predefined command' })}
			options={predefinedOptions}
			value={selectedOption}
			on:change={handlePresetChange}
			disabled={!canSend}
		/>
	{:else}
		<Select
			id="brpc-preset"
			icon={null}
			aria-label={$format('brpc_preset_aria', { default: 'Predefined command' })}
			label={$format('brpc_preset_label', { default: 'Predefined command' })}
			options={predefinedOptions}
			value={selectedOption}
			on:change={handlePresetChange}
			disabled={!canSend}
		/>
	{/if}

	<div class="d-flex justify-content-end">
		<svelte:component this={ButtonC} on:click={sendPredefined} disabled={!canSend}>
			{pairingInProgress
				? $format('brpc_button_pairing', { default: 'Pairing…' })
				: loading
					? $format('brpc_button_sending', { default: 'Sending…' })
					: $format('brpc_button_send_preset', { default: 'Send selected command' })}
		</svelte:component>
	</div>
</div>

<hr class="mt-0 mb-m" />

<!-- Custom command section -->
<Text weight="bold">
	{$format('brpc_custom_title', { default: 'Custom RPC' })}
</Text>
<Text styleType="body-small" color="secondary" class="mt-xs mb-s">
	{$format('brpc_custom_subtitle', { default: 'Compose any RPC method and JSON parameters.' })}
</Text>

<div class="brpc-stack mb-m">
	<svelte:component
		this={InputC}
		id="brpc-method"
		icon={null}
		label={$format('brpc_method_label', { default: 'Method' })}
		placeholder="e.g. Shelly.GetStatus"
		bind:value={customMethod}
		disabled={!canSend}
		aria-label={$format('brpc_method_label', { default: 'Method' })}
	/>

	<div>
		<Text styleType="body-small" weight="bold" class="mb-xs">
			{$format('brpc_params_label', { default: 'Params (JSON)' })}
		</Text>
		<textarea
			id="brpc-params"
			class="brpc-textarea"
			rows="4"
			placeholder={'{ }'}
			bind:value={customParamsInput}
			disabled={!canSend}
			aria-label={$format('brpc_params_label', { default: 'Params (JSON)' })}
		/>
	</div>

	{#if customParamsError}
		{#if useNewLayout}
			<AlertV2 customIcon="fas fa-exclamation-circle" heading={customParamsError} />
		{:else}
			<Alert type="danger" heading={customParamsError} />
		{/if}
	{/if}

	<div class="d-flex justify-content-end">
		<svelte:component this={ButtonC} variant="primary" on:click={sendCustom} disabled={!canSend}>
			{pairingInProgress
				? $format('brpc_button_pairing', { default: 'Pairing…' })
				: loading
					? $format('brpc_button_sending', { default: 'Sending…' })
					: $format('brpc_button_send_custom', { default: 'Send custom RPC' })}
		</svelte:component>
	</div>
</div>

<hr class="mt-0 mb-m" />

<!-- History / response viewer -->
<div class="d-flex justify-content-between align-items-center mb-s">
	<Text weight="bold">
		{$format('brpc_history_title', { default: 'Recent calls' })}
	</Text>
	{#if history.length > 0}
		<svelte:component
			this={ButtonC}
			size="s"
			variant="secondary"
			on:click={clearHistory}
			disabled={loading}
		>
			{$format('brpc_clear_history', { default: 'Clear' })}
		</svelte:component>
	{/if}
</div>

{#if history.length === 0}
	{#if useNewLayout}
		<AlertV2
			heading={$format('brpc_no_history_heading', { default: 'No calls yet' })}
			message={$format('brpc_no_history_message', {
				default: 'Send a predefined or custom RPC above to see the response here.',
			})}
		/>
	{:else}
		<Alert
			type="info"
			heading={$format('brpc_no_history_heading', { default: 'No calls yet' })}
			message={$format('brpc_no_history_message', {
				default: 'Send a predefined or custom RPC above to see the response here.',
			})}
		/>
	{/if}
{:else}
	{#each history as entry (entry.id)}
		{@const isPending = entry.response === null && entry.error === null}
		{@const subtitleText = historySubtitle(entry)}
		<Collapse
			id={`brpc-history-${entry.id}`}
			class="collapse-title-left-align"
			title={`${entry.method} — ${subtitleText}`}
			expanded={openHistoryId === entry.id}
			on:click={() => toggleHistory(entry.id)}
		>
			<div class="brpc-stack">
				<div>
					<div class="d-flex justify-content-between align-items-center mb-xs">
						<Text styleType="body-small" weight="bold">
							{$format('brpc_request_label', { default: 'Request frame' })}
						</Text>
						<IconButton
							icon="fas fa-copy"
							variant="secondary"
							aria-label={capFirstChar(
								$format('brpc_copy_request', { default: 'Copy request frame' }),
							)}
							on:click={() => copyEntry(entry.frame)}
						/>
					</div>
					<pre class="brpc-code">{entry.frame}</pre>
				</div>

				<div>
					<div class="d-flex justify-content-between align-items-center mb-xs">
						<Text styleType="body-small" weight="bold">
							{$format('brpc_response_label', { default: 'Response' })}
						</Text>
						{#if entry.response}
							<IconButton
								icon="fas fa-clipboard"
								variant="secondary"
								aria-label={capFirstChar(
									$format('brpc_copy_response', { default: 'Copy response' }),
								)}
								on:click={() => copyEntry(entry.response ?? '')}
							/>
						{/if}
					</div>
					{#if isPending}
						<div class="d-flex align-items-center gap-s">
							<Loader />
							<Text styleType="body-small" color="secondary">
								{$format('brpc_history_waiting', {
									default: 'Waiting for response…',
								})}
							</Text>
						</div>
					{:else if entry.error}
						{#if useNewLayout}
							<AlertV2 customIcon="fas fa-exclamation-circle" heading={entry.error} />
						{:else}
							<Alert type="danger" heading={entry.error} />
						{/if}
					{:else}
						<pre class="brpc-code brpc-code--response">{entry.response}</pre>
					{/if}
				</div>
			</div>
		</Collapse>
	{/each}
{/if}

<style>
	.brpc-stack {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-s);
	}

	.brpc-textarea {
		width: 100%;
		padding: var(--spacing-s) var(--spacing-m);
		border: 1px solid var(--color-border-default);
		border-radius: var(--border-radius-s);
		background: var(--color-background-input);
		color: var(--color-text-default);
		font-size: var(--font-size-body-small);
		font-family: var(--font-family-mono, monospace);
		resize: vertical;
		box-sizing: border-box;
	}

	.brpc-textarea:focus {
		outline: none;
		border-color: var(--color-border-focus);
	}

	.brpc-textarea:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.brpc-code {
		margin: 0;
		padding: var(--spacing-s) var(--spacing-m);
		background: var(--color-background-code, var(--color-background-secondary));
		border: 1px solid var(--color-border-default);
		border-radius: var(--border-radius-s);
		font-family: var(--font-family-mono, monospace);
		font-size: var(--font-size-body-small);
		color: var(--color-text-muted);
		white-space: pre-wrap;
		word-break: break-all;
		overflow: auto;
		max-height: 300px;
	}

	.brpc-code--response {
		color: var(--color-text-default);
		max-height: 360px;
	}

	/**
	 * Even out the spacing between history entries so the list reads as a
	 * clean stack — the design-system Collapse doesn't ship its own
	 * inter-item gap. `:not(:last-child)` keeps the bottom edge flush so
	 * we don't add dead space below the final entry.
	 */
	:global(.collapse-title-left-align:not(:last-child)) {
		margin-bottom: 8px;
	}
</style>
